/**
 * Submits document.
 *
 * @param {File} file - The file to upload.
 * @param {string} url - service url.
 * @param {string} httpMethod - http method to use
 *
 * @return {Object} - json response.
 */
function submitDocument(file, url, httpMethod) {
  const data = new FormData();
  data.append('file', file);
  return fetch(url, {
    method: httpMethod,
    body: data
  })
    .then((response) => {
      return response.json();
    })
    .catch((error) => {
      console.error('Error:', error);
      return {};
    });
}
/**
 * Gets resource as json response.
 *
 * @param {string} url - service url.
 *
 * @return {Object} - json response.
 */
function getResourceWithJSONResponse(url) {
  return fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  }).then((response) => {
    return response.json();
  });
}

/**
 * Uploads a file and creates a new document from it.
 *
 * @param {File} file - The file to upload.
 * @param {string} archiveId - the number of the selected archive
 */
export async function createDocument(file, archiveId) {
  return submitDocument(file, '/documents?archive=' + archiveId, 'POST');
}

/**
 * Uploads and validates a file to get the validation result infos.
 *
 * @param {File} file - The file to upload.
 */
export async function validateDocument(file) {
  return submitDocument(file, '/document-info/validate', 'POST');
}

/**
 * Fetch document informations from service.
 *
 * @param {string} documentId - The path for the new file.
 */
export async function getDocumentInfo(documentId) {
  return getResourceWithJSONResponse('/documents/' + documentId);
}

/**
 * Updates the document with the provided metadata.
 *
 * @param {Object} metadata - The metadata to be added to the document.
 * @param {string} documentId - The id of the uploaded document.
 * @param {string} archiveId - the archive id
 */
export async function updateDocument(metadata, documentId, archiveId) {
  return fetch('/documents/' + documentId + '?archive=' + archiveId, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(metadata)
  }).then((response) => {
    return response.json();
  });
}

/**
 * Add a document party for a given object id.
 *
 * @param {Object} documentParty - The metadata to be added to the document.
 * @param {string} documentId - The id of the uploaded document.
 */
export async function addParty(documentParty, documentId) {
  return fetch('/documents/' + documentId + '/parties', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(documentParty)
  }).then((response) => {
    return response.json();
  });
}

/**
 * Update a document party for a given object id.
 *
 * @param {Object} documentParty - The metadata to be added to the document.
 * @param {string} documentId - The id of the uploaded document.
 */
export async function updateParty(documentParty, documentId) {
  return fetch('/documents/' + documentId + '/parties/' + documentParty.id, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(documentParty)
  }).then((response) => {
    return response.json();
  });
}

/**
 * Delete a document party for a given document- and party id.
 *
 * @param {string} partyId - The metadata to be added to the document.
 * @param {string} documentId - The id of the uploaded document.
 */
export async function deleteParty(partyId, documentId) {
  return fetch('/documents/' + documentId + '/parties/' + partyId, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json'
    }
  }).then((response) => {
    return response.json();
  });
}

/**
 * Delete a document party for a given document- and party id.
 *
 * @param {string} documentId - The id of the uploaded document.
 */
export async function getParties(documentId) {
  return getResourceWithJSONResponse('/documents/' + documentId + '/parties/');
}

/**
 * This function fetch a folder with a given ID from remote server.
 * @param {string} folderId - The folder ID.
 * @param {number} pageIndex - The page index.
 * @param {number} pageSize - The page size.
 */
export async function getFolderById(folderId, pageIndex, pageSize) {
  return getResourceWithJSONResponse(
    '/folders/' +
      folderId +
      '?page-index=' +
      pageIndex +
      '&page-size=' +
      pageSize
  );
}

/**
 * This function fetch the notary root folder from remote server.
 *
 * @param {string} archiveId
 */
export async function getRootFolder(archiveId) {
  return getResourceWithJSONResponse('/archives/' + archiveId + '/folders');
}

/**
 * Search for documents.
 *
 * @param {string} archiveId
 * @param {Object} searchParameter
 * @param {number} pageIndex - The page index.
 * @param {number} pageSize - The page size.
 * @return {Object} response
 */
export function search(archiveId, searchParameter, pageIndex, pageSize) {
  let url = '/archives/' + archiveId + '/search';
  if (pageIndex || pageSize) {
    url += '?';
    if (pageIndex) {
      url += 'page-index=' + pageIndex;
    }
    if (pageSize) {
      if (pageIndex) {
        url += '&';
      }
      url += 'page-size=' + pageSize;
    }
  }

  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(searchParameter)
  }).then((response) => {
    return response.json();
  });
}
/**
 * Creates a new document version.
 *
 * @param {File} file - The new version to upload.
 * @param {string} documentId - The id of the document.
 * @param {string} archiveId
 */
export async function createDocumentVersion(file, documentId, archiveId) {
  return submitDocument(
    file,
    '/documents/' + documentId + '/version' + '?archive=' + archiveId,
    'PUT'
  );
}

/**
 * Gets document's versions
 *
 * @param {string} documentId - The id of the document.
 */
export async function getVersions(documentId) {
  return getResourceWithJSONResponse('/documents/' + documentId + '/versions');
}

/**
 * Gets person's documents count
 */
export async function getPersonDocuments() {
  return getResourceWithJSONResponse('/documents/person/count');
}

/**
 * Gets all person's documents.
 *
 * @param {string} includeDocuments - 'true' if document list should be included,
 * 'false' other empty string otherwise.
 */
export async function getAllPersonDocuments(includeDocuments) {
  return getResourceWithJSONResponse(
    '/documents/person/count?includeDocuments=' + includeDocuments
  );
}
